import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { rem } from 'polished'
import { ChevronRight as MaterialChevronRight } from 'emotion-icons/material'
import AsyncSelect from 'react-select/async'
import Select from 'react-select'

import { formMixin } from './formMixins'
import Button from '../Button'
import Typography from '../Typography'
import Brandy from '../Brandy'

export const FormContainer = styled.div`
  max-width: 450px;
  width: 100%;
`

export const FormTemplate = styled.div(formMixin)

export const Input = styled.input(({ theme, hasError, isTouchedAndValid }) => {
  let borderColor = theme.colors.light.darker.hex

  if (hasError) {
    borderColor = theme.colors.error.base.hex
  }
  if (isTouchedAndValid) {
    borderColor = theme.colors.success.base.hex
  }

  return css`
    padding: ${theme.spacing.xxs} ${theme.spacing.xs};
    border: 1px solid ${borderColor};
    border-radius: ${theme.radius};
    background-color: white;
    width: 100%;

    // TODO: This is a temporary hotfix and should be removed
    // when a better colorway solution is done.
    color: #000 !important;

    /* This removes the arrows from the number field */
    &[type='number'] {
      -moz-appearance: textfield;

      &::-webkit-inner-spin-button,
      &::-webkit-outer-spin-button {
        appearance: none;
        margin: 0;
      }
    }

    &:invalid {
      box-shadow: none;
    }

    &[type='radio'],
    &[type='checkbox'] {
      width: ${rem(theme.fonts.body.md.size)};
      height: ${rem(theme.fonts.body.md.size)};
    }
  `
})

export const AddressSelect = styled(AsyncSelect)`
  div[tabindex='-1'] { color: #000 !important; }

  > div {
    border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
      // eslint-disable-next-line
      hasError
        ? `${theme.colors.error.base.hex};`
        : isTouchedAndValid
        ? `${theme.colors.success.base.hex};`
        : `${theme.colors.light.darker.hex};`}

    &:hover {
      border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
        // eslint-disable-next-line
        hasError
          ? `${theme.colors.error.base.hex};`
          : isTouchedAndValid
          ? `${theme.colors.success.base.hex};`
          : `${theme.colors.light.darker.hex};`}
    }

    border-radius: ${({ theme }) => `${theme.radius}`};
  }
`

export const AddressSelectGroup = styled(AsyncSelect)`
  width: 100%;

  div[tabindex='-1'] { color: #000 !important; }

  .address-select-group__control { height: 100%; }

  .address-select-group__indicators { display: none; }

  > div {
    border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
      // eslint-disable-next-line
      hasError
        ? `${theme.colors.error.base.hex};`
        : isTouchedAndValid
        ? `${theme.colors.success.base.hex};`
        : `${theme.colors.light.darker.hex};`}

    &:hover {
      border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
        // eslint-disable-next-line
        hasError
          ? `${theme.colors.error.base.hex};`
          : isTouchedAndValid
          ? `${theme.colors.success.base.hex};`
          : `${theme.colors.light.darker.hex};`}
    }

    border-radius: ${({ theme }) => `${theme.radius}`};
    padding-right: 3px;
  }
`

export const SelectDropdown = styled(Select)`
 div[tabindex='-1'] { color: #000 !important; }

  > div {
    border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
      // eslint-disable-next-line
      hasError
        ? `${theme.colors.error.base.hex};`
        : isTouchedAndValid
        ? `${theme.colors.success.base.hex};`
        : `${theme.colors.light.darker.hex};`}

    &:hover {
      border: 1px solid ${({ theme, hasError, isTouchedAndValid }) =>
        // eslint-disable-next-line
        hasError
          ? `${theme.colors.error.base.hex};`
          : isTouchedAndValid
          ? `${theme.colors.success.base.hex};`
          : `${theme.colors.light.darker.hex};`}
    }

    border-radius: ${({ theme }) => `${theme.radius}`};
  }
`

export const SubmitButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  margin-top: 10px;
  grid-area: submit_button;

  span {
    font-size: ${({ theme }) => rem(theme.fonts.body.lg.size)};
  }
`

export const StepButton = styled(Button)`
  position: relative;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  width: 100%;
  text-align: left;
  justify-content: flex-start;
  grid-area: ${({ gridArea }) => gridArea};
`

export const ButtonText = styled.span`
  font-size: ${({ theme }) => rem(theme.fonts.body.lg.size)};
`

export const ChevronRight = styled(MaterialChevronRight)`
  position: absolute;
  right: 15px;
`

export const Label = styled.label(
  ({ theme, gridArea }) => css`
    padding-bottom: ${theme.spacing.sm};
    display: block;
    position: relative;
    grid-area: ${gridArea};
  `
)

/* TODO: The grid area should be applied directly to the disclaimer,
    but for some strange reason that component wasn't allowing the
    style override. I created a backlog ticket for it here:
    https://clearlink.atlassian.net/browse/CLCMS-902 */
export const FormDisclaimer = styled(Brandy)`
  grid-area: form_disclaimer;
`

export const LabelText = styled.span`
  display: block;
  font-size: ${({ theme }) => rem(theme.fonts.body.sm.size)};
  margin-left: ${({ theme }) => theme.spacing.xxs};
`

export const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => rem(theme.fonts.body.md.size)};
  cursor: pointer;
  padding-bottom: ${({ theme }) => theme.spacing.xxs};
  grid-area: ${({ gridArea }) => gridArea};
`

export const ErrorText = styled.span`
  display: block;
  position: absolute;
  bottom: 0;
  left: 0;
  font-size: ${({ theme }) => rem(theme.fonts.body.xs.size)};
  color: ${({ theme }) => theme.colors.error.base.hex};
`
export const Group = styled.div`
  input {
    width: 100%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  button {
    min-width: 0;
    flex-shrink: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    display: flex;

    button {
      margin: 0;
      width: auto;

      &:last-of-type {
        border-top-left-radius: 0;
        border-top-right-radius: ${({ theme }) => theme.radius};
        border-bottom-left-radius: 0;
      }
    }

    input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: ${({ theme }) => theme.radius};
    }
  }
`

export const AddressGroup = styled.div`
  button {
    min-width: 0;
    flex-shrink: 0;
    border-top-left-radius: ${({ theme }) => theme.radius};
    border-top-right-radius: ${({ theme }) => theme.radius};
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
    display: flex;

    button {
      margin: 0;
      width: auto;
      position: relative;
      left: -3px;

      &:last-of-type {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`

export const OptionGroup = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  grid-area: ${({ gridArea }) => gridArea};
  position: relative;
`

export const StepCounter = styled(Typography)`
  grid-area: step_counter;
`

export const TCPADisclaimerContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: baseline;

  .options_tcpa-checkbox {
    min-width: 15px;
    min-height: 15px;
    margin: 0;
    position: relative;
    top: 5px;
  }

  .leshen-brandy {
    margin-top: 14px;
    margin-bottom: 0;
  }

  .error-text {
    bottom: 30px;
    left: 38px;
  }
`
